import { EditorState } from "@codemirror/state";
import { EditorView, keymap, lineNumbers, placeholder } from "@codemirror/view";
import {
  highlightSpecialChars,
  drawSelection,
  dropCursor,
  rectangularSelection
} from "@codemirror/view";
import {
  defaultHighlightStyle,
  syntaxHighlighting,
  indentOnInput,
  bracketMatching,
  foldGutter,
  foldKeymap,
  LRLanguage
} from "@codemirror/language";
import { defaultKeymap, history, historyKeymap } from "@codemirror/commands";
import { searchKeymap, highlightSelectionMatches } from "@codemirror/search";
import {
  autocompletion,
  completionKeymap,
  closeBrackets,
  closeBracketsKeymap,
  CompletionContext,
  CompletionSource
} from "@codemirror/autocomplete";
import { lintKeymap } from "@codemirror/lint";
import { indentWithTab } from "@codemirror/commands";
import { dracula } from "@uiw/codemirror-theme-dracula";
import { colorPicker } from "@replit/codemirror-css-color-picker";
import {
  abbreviationTracker,
  emmetConfig,
  EmmetKnownSyntax
} from "@emmetio/codemirror6-plugin";
import { createFocusAtPositionHandler } from "./focus-at-position";
import { emitCursorPosition } from "./emit-cursor-position";
import {
  completeTwigVariables,
  createTwigVariablesCompletions
} from "./twig-variables-completions";
import {
  completeTags,
  createTagsCompletions,
  specialTagsParser
} from "./tags-completions";
import { createAutofillOnEmpty } from "./autofill";
import { createKeyboardFocus } from "./keyboard-focus";
import { createErrorLineHighlighter } from "./error-line-highlighter";
import { twigSpecialWordsParser } from "./twig-macro-highlighter";
import { css, cssLanguage } from "@codemirror/lang-css";
import { javascript, javascriptLanguage } from "@codemirror/lang-javascript";
import { php, phpLanguage } from "@codemirror/lang-php";
import { html, htmlLanguage } from "@codemirror/lang-html";
import { twig, twigLanguage } from "@ssddanbrown/codemirror-lang-twig";
import { cssVariablesCompletions } from "./css-variables-completions";
import { cssSelectorsCompletions } from "./css-selectors-completions";
import { twigHtml, mixedHtmlTwigLanguage } from "./twig-language";
import { CodeInputLanguage } from "@/components/Reusable/CustomInputs/CodeInput/CodeInput.vue";
import { htmlLintExtension, cssLintExtension } from "./code-lint";

export function createBaseExtensions(
  language: CodeInputLanguage,
  onCursorPosition: (position: number) => void,
  onUpdate: (value: string) => void,
  onFormat: () => void,
  isUpdatingFromProp: boolean,
  placeholderText: string,
  showLineNumbers: boolean,
  firstLineNumber: number,
  readOnly: boolean,
  disableLineWrapping: boolean,
  focusAtPosition: number | null,
  twigVariables: string[],
  tags: string[],
  autofillOnEmpty: string | null,
  highlightErrorLine: number | null
) {
  const languageDefinition = getLanguageDefinition(language);

  const changeEvent = EditorView.updateListener.of(update => {
    // TODO: isUpdatingFromProp is not working as expected
    if (update.docChanged && !isUpdatingFromProp) {
      const newValue = update.state.doc.toString();
      onUpdate(newValue);
    }
  });

  const emmetSyntaxes: Record<string, EmmetKnownSyntax> = {
    twig: EmmetKnownSyntax.html,
    "twig/html": EmmetKnownSyntax.html,
    "twig/css": EmmetKnownSyntax.css
  };

  const extensions = [
    highlightSpecialChars(),
    history(),
    foldGutter(),
    drawSelection(),
    dropCursor(),
    EditorState.allowMultipleSelections.of(true),
    indentOnInput(),
    syntaxHighlighting(defaultHighlightStyle, {
      fallback: true
    }),
    bracketMatching(),
    closeBrackets(),
    autocompletion(),
    rectangularSelection(),
    highlightSelectionMatches(),
    keymap.of([
      ...closeBracketsKeymap,
      ...defaultKeymap,
      ...searchKeymap,
      ...historyKeymap,
      ...foldKeymap,
      ...completionKeymap,
      ...lintKeymap,
      {
        key: "Mod-Alt-l",
        run: () => {
          onFormat();
          return true;
        }
      }
    ]),
    colorPicker,
    dracula,
    emmetConfig.of({
      previewEnabled: false,
      syntax: emmetSyntaxes[language] || undefined
    }),
    abbreviationTracker({
      autocompleteTab: true,
      syntax: emmetSyntaxes[language] || undefined
    }),
    keymap.of([indentWithTab]),
    changeEvent
  ];

  const focusHandler = createFocusAtPositionHandler(focusAtPosition);
  if (focusHandler) {
    extensions.push(focusHandler);
  }

  const cursorPositionHandler = emitCursorPosition(onCursorPosition);
  extensions.push(cursorPositionHandler);

  // Add autofill handler
  const autofillHandler = createAutofillOnEmpty(autofillOnEmpty);
  extensions.push(autofillHandler);

  // Add keyboard focus handler
  const keyboardFocusHandler = createKeyboardFocus();
  extensions.push(keyboardFocusHandler);

  // Add error line highlighter
  const errorLineHighlighter = createErrorLineHighlighter(highlightErrorLine);
  extensions.push(errorLineHighlighter);

  // Conditionally add line numbers with custom starting number
  if (showLineNumbers) {
    extensions.push(
      lineNumbers({
        formatNumber: (lineNo: number) =>
          (lineNo + firstLineNumber - 1).toString()
      })
    );
  }

  if (readOnly) {
    extensions.push(EditorState.readOnly.of(true));
  }

  if (!disableLineWrapping) {
    extensions.push(EditorView.lineWrapping);
  }

  if (placeholderText) {
    extensions.push(placeholder(placeholderText));
  }

  // Helper function to add completion sources
  const addCompletionSources = (isTwigLanguage: boolean) => {
    const completionSources: CompletionSource[] = [];

    // Add Twig variables completion if available
    if (twigVariables?.length > 0) {
      if (isTwigLanguage) {
        completionSources.push((context: CompletionContext) =>
          completeTwigVariables(context, twigVariables)
        );
      } else {
        extensions.push(
          createTwigVariablesCompletions(twigVariables, languageDefinition)
        );
      }
    }

    // Add tags completion if available
    if (tags?.length > 0) {
      if (isTwigLanguage) {
        completionSources.push((context: CompletionContext) =>
          completeTags(context, tags)
        );
        extensions.push(specialTagsParser);
      } else {
        extensions.push(createTagsCompletions(tags, languageDefinition));
      }
    }

    // Add autocompletion extension if we have sources (twig only)
    if (isTwigLanguage && completionSources.length > 0) {
      extensions.push(
        autocompletion({
          override: completionSources
        })
      );
    }
  };

  // Add completion sources based on language
  addCompletionSources(language === "twig/html");

  // Add language-specific extensions
  const languageExtension = getLanguageExtension(language);
  if (languageExtension) {
    if (Array.isArray(languageExtension)) {
      extensions.push(...languageExtension);
    } else {
      extensions.push(languageExtension);
    }
  }

  // Add Twig highlighter for Twig-related languages
  if (
    language === "twig" ||
    language === "twig/html" ||
    language === "twig/css"
  ) {
    extensions.push(twigSpecialWordsParser);
  }

  return extensions;
}

function getLanguageDefinition(language: CodeInputLanguage): LRLanguage {
  switch (language) {
    case "css":
      return cssLanguage;
    case "javascript":
      return javascriptLanguage;
    case "x-php":
      return phpLanguage;
    case "php":
      return phpLanguage;
    case "html":
      return htmlLanguage;
    case "twig":
      return twigLanguage;
    case "twig/html":
      return mixedHtmlTwigLanguage;
    case "twig/css":
      return twigLanguage;
    default:
      return htmlLanguage; // fallback
  }
}

function getLanguageExtension(language: CodeInputLanguage) {
  switch (language) {
    case "css":
      return [
        css(),
        cssVariablesCompletions,
        cssSelectorsCompletions,
        cssLintExtension
      ];
    case "javascript":
      return javascript();
    case "x-php":
      return php();
    case "php":
      return php({
        plain: true,
        baseLanguage: null
      });
    case "html":
      return [html(), htmlLintExtension];
    case "twig":
      return twig();
    case "twig/html":
      return twigHtml();
    case "twig/css":
      return twig();
    default:
      return null;
  }
}
